// langues supportées
export const supportedLanguages = ['fr', 'en', 'ar']

// détection langue navigateur
function getBrowserLang() {
  if (typeof navigator === 'undefined') return 'en'

  const lang = navigator.language?.split('-')[0]
  return supportedLanguages.includes(lang) ? lang : 'en'
}

export function scrollBehavior(to, from, savedPosition) {
  // back/forward → restaure la position
  if (savedPosition) return savedPosition
  if (to.hash) return { el: to.hash, behavior: 'smooth' }

  // Changement de page comme changement de langue (depuis le footer par
  // exemple) : la remontée en haut se fait PENDANT le fondu de page
  // (mode out-in, 280ms de leave) — la page est invisible à ce moment-là,
  // donc aucun saut ni flash perceptible, juste un fondu doux qui rouvre
  // en haut de page.
  if (typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    return { top: 0, left: 0 }
  }
  return new Promise((resolve) => {
    setTimeout(() => resolve({ top: 0, left: 0 }), 300)
  })
}

export const routes = [
    // --- racine → langue navigateur ---
    {
      path: '/',
      redirect: () => `/${getBrowserLang()}`
    },

    // --- routes localisées ---
    {
      path: '/:lang',
      children: [
        {
          path: '',
          name: 'home',
          component: () => import('../pages/HomePage.vue')
        },
        {
          path: 'platform',
          name: 'platform',
          component: () => import('../pages/Platform.vue')
        },
        {
          path: 'why-bifrost',
          name: 'why-bifrost',
          component: () => import('../pages/WhyBifrost.vue')
        },
        {
          path: 'philosophy',
          name: 'philosophy',
          component: () => import('../pages/Philosophy.vue')
        },
        {
          path: 'terms',
          name: 'terms',
          component: () => import('../pages/Terms.vue')
        },
        {
          path: 'privacy',
          name: 'privacy',
          component: () => import('../pages/Privacy.vue')
        },
        {
          path: 'solutions',
          name: 'solutions',
          children: [
            {
              path: 'pharma',
              name: 'solutions-pharma',
              component: () => import('@/pages/solutions/Pharma.vue')
            },
            {
              path: 'corporate',
              name: 'solutions-corporate',
              component: () => import('@/pages/solutions/Corporate.vue')
            },
            {
              path: 'institutions',
              name: 'solutions-institutions',
              component: () => import('@/pages/solutions/Institutions.vue')
            },
          ]
        },
        {
          path: 'partners',
          name: 'partners',
          children: [
            {
              path: 'agencies',
              name: 'agencies',
              component: () => import('@/pages/partners/Agencies.vue')
            },
            {
              path: 'technology',
              name: 'technology',
              component: () => import('@/pages/partners/Technology.vue')
            },
          ]
        },
      ]
    },

    // --- under construction ---
    {
      path: '/under-construction',
      name: 'under-construction',
      component: () => import('../pages/UnderConstruction.vue')
    },

    // --- fallback ---
    {
      path: '/:pathMatch(.*)*',
      redirect: () => `/${getBrowserLang()}`
    }
]

// Guards enregistrés par l'entrée ViteSSG (main.js) — le router est créé
// par vite-ssg, plus par ce module. L'instance i18n est celle de l'app
// courante (une par page rendue en SSG, cf. src/i18n.js).
export function setupRouterGuards(router, i18n) {
  router.beforeEach((to, from, next) => {
  const browserLang = getBrowserLang()
  const langParam = to.params.lang

  // --- under construction ON ---
  if (
    import.meta.env.VITE_UNDER_CONSTRUCTION === 'true' &&
    to.name !== 'under-construction'
  ) {
    return next({ name: 'under-construction' })
  }

  // --- under construction OFF ---
  if (
    import.meta.env.VITE_UNDER_CONSTRUCTION === 'false' &&
    to.name === 'under-construction'
  ) {
    return next(`/${browserLang}`)
  }

  // --- langue invalide ---
  if (langParam && !supportedLanguages.includes(langParam)) {
    return next(`/${browserLang}`)
  }

  const effectiveLang = langParam || browserLang

  // Changement de langue en cours de session (footer, back/forward) :
  // la bascule i18n est différée à la fin du fondu de sortie
  // (App.vue @after-leave) pour que l'ancienne page ne se retraduise
  // pas "en direct" à l'écran. Au premier chargement (from.name absent),
  // on synchronise immédiatement.
  const deferredLangSwitch = !!from.name && to.params.lang !== from.params.lang

  // --- sync i18n ---
  if (i18n?.global && !deferredLangSwitch) {
    if (typeof i18n.global.locale === 'object') {
      i18n.global.locale.value = effectiveLang
    } else {
      i18n.global.locale = effectiveLang
    }
  }

  // --- html lang + dir --- (suit la même temporisation via le watcher
  // de locale dans App.vue quand la bascule est différée)
  if (typeof document !== 'undefined' && !deferredLangSwitch) {
    document.documentElement.lang = effectiveLang
    document.documentElement.dir = effectiveLang === 'ar' ? 'rtl' : 'ltr'
  }

  next()
  })
}
